import { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import LanguageSelector, {
  getLanguageInfo,
  SUPPORTED_LANGUAGES,
} from "@/common/LanguageSelector";

import type { DraftState } from "./types";

interface Props {
  draft: DraftState;
  isSaving: boolean;
  translationLanguages: string[];
  onUpdateDraft: (updates: Partial<DraftState>) => void;
  onUpdateLangContent: (lang: string, value: string) => void;
  onUpdateTranslation: (lang: string) => void;
  onAddLang: () => void;
  onAutoTranslate: (lang: string) => void;
  onDeleteLang: (lang: string) => void;
}

const AddLangButton = ({
  ariaLabel,
  onClick,
}: {
  ariaLabel: string;
  onClick: () => void;
}) => (
  <IconButton
    size="small"
    onClick={onClick}
    aria-label={ariaLabel}
    sx={{ color: "text.secondary" }}
  >
    <AddIcon fontSize="small" />
  </IconButton>
);

const TemplateTranslations = ({
  draft,
  isSaving,
  translationLanguages,
  onUpdateDraft,
  onUpdateLangContent,
  onUpdateTranslation,
  onAddLang,
  onAutoTranslate,
  onDeleteLang,
}: Props) => {
  const { t } = useTranslation("message");
  const [deleteLang, setDeleteLang] = useState<string | null>(null);
  const translationContents = draft.contents.filter((c) => c.lang !== "en");
  const autoLangSet = new Set(
    translationContents.filter((c) => c.is_ai_translated).map((c) => c.lang),
  );
  const allLangs = translationContents.map((c) => c.lang);
  const addableLanguages = SUPPORTED_LANGUAGES.filter(
    (l) =>
      translationLanguages.includes(l.code) &&
      !draft.contents.some((c) => c.lang === l.code),
  );

  const toggleLang = (lang: string) =>
    onUpdateDraft({
      expandedLang: draft.expandedLang === lang ? null : lang,
      addingLang: false,
    });

  const toggleAddingLang = () =>
    onUpdateDraft({ addingLang: !draft.addingLang, expandedLang: null });

  return (
    <Box>
      {/* Accordion header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          width: "fit-content",
          gap: "6px",
        }}
        onClick={() =>
          onUpdateDraft({
            translationsOpen: !draft.translationsOpen,
            expandedLang: draft.translationsOpen ? null : (allLangs[0] ?? null),
            addingLang: false,
          })
        }
      >
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{ letterSpacing: "0.17px" }}
        >
          {t("settings.translations.translationsCount", {
            count: translationContents.length,
          })}
        </Typography>
        {draft.translationsOpen ? (
          <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
        )}
      </Box>

      <Collapse in={draft.translationsOpen}>
        {/* Language select row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            mt: "12px",
            mb: draft.expandedLang ? "12px" : 0,
          }}
        >
          {allLangs.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>{t("settings.translations.language")}</InputLabel>
              <Select
                label={t("settings.translations.language")}
                value={
                  allLangs.includes(draft.expandedLang ?? "")
                    ? draft.expandedLang!
                    : ""
                }
                onChange={(e: SelectChangeEvent<string>) =>
                  toggleLang(e.target.value)
                }
                renderValue={(val) => (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {getLanguageInfo(val)?.name ?? val}
                    {autoLangSet.has(val) && (
                      <Chip
                        icon={<AutoFixHighIcon />}
                        label={t("settings.translations.ai")}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: 10,
                          "& .MuiChip-icon": { fontSize: 12 },
                        }}
                      />
                    )}
                  </Box>
                )}
              >
                {allLangs.map((lang) => (
                  <MenuItem key={lang} value={lang}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        width: "100%",
                      }}
                    >
                      <span style={{ flex: 1 }}>
                        {getLanguageInfo(lang)?.name ?? lang}
                      </span>
                      {autoLangSet.has(lang) && (
                        <Chip
                          icon={<AutoFixHighIcon />}
                          label={t("settings.translations.ai")}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: 10,
                            "& .MuiChip-icon": { fontSize: 12 },
                          }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {addableLanguages.length > 0 && (
            <AddLangButton
              ariaLabel={t("settings.translations.addTranslationLanguage")}
              onClick={toggleAddingLang}
            />
          )}
        </Box>

        {/* Per-language editor */}
        {draft.expandedLang && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label={t("settings.translations.translation")}
              value={
                draft.contents.find((c) => c.lang === draft.expandedLang)
                  ?.content ?? ""
              }
              onChange={(e) =>
                onUpdateLangContent(draft.expandedLang!, e.target.value)
              }
              onBlur={() => onUpdateTranslation(draft.expandedLang!)}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Button
                color="error"
                size="small"
                disabled={isSaving}
                startIcon={<DeleteOutlineIcon fontSize="small" />}
                onClick={() => setDeleteLang(draft.expandedLang)}
              >
                {t("settings.translations.deleteTranslation")}
              </Button>
              <Tooltip
                title={t("settings.translations.autoTranslateTo", {
                  language:
                    getLanguageInfo(draft.expandedLang)?.name ??
                    draft.expandedLang,
                })}
              >
                <span>
                  <Button
                    size="small"
                    disabled={isSaving}
                    startIcon={
                      isSaving ? (
                        <CircularProgress size={14} />
                      ) : (
                        <AutoFixHighIcon fontSize="small" />
                      )
                    }
                    onClick={() => onAutoTranslate(draft.expandedLang!)}
                  >
                    {t("settings.translations.autoTranslate")}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
        )}

        {/* Add new language form */}
        {draft.addingLang && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              mt: "12px",
              p: "12px",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <LanguageSelector
              value={draft.newLangCode}
              onChange={(code) => onUpdateDraft({ newLangCode: code })}
              columns={2}
              languages={addableLanguages}
            />
            <TextField
              label={t("settings.translations.translation")}
              multiline
              minRows={2}
              fullWidth
              value={draft.newLangContent}
              onChange={(e) =>
                onUpdateDraft({ newLangContent: e.target.value })
              }
            />
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                size="small"
                onClick={() =>
                  onUpdateDraft({
                    addingLang: false,
                    newLangCode: "",
                    newLangContent: "",
                  })
                }
              >
                {t("settings.cancel")}
              </Button>
              <Tooltip
                title={
                  !draft.newLangCode.trim()
                    ? t("settings.translations.selectLanguageFirst")
                    : t("settings.translations.autoTranslateTo", {
                        language:
                          getLanguageInfo(draft.newLangCode)?.name ??
                          draft.newLangCode,
                      })
                }
              >
                <span>
                  <Button
                    size="small"
                    disabled={isSaving || !draft.newLangCode.trim()}
                    startIcon={
                      isSaving ? (
                        <CircularProgress size={14} />
                      ) : (
                        <AutoFixHighIcon fontSize="small" />
                      )
                    }
                    onClick={() => onAutoTranslate(draft.newLangCode)}
                  >
                    {t("settings.translations.autoTranslate")}
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                size="small"
                disabled={
                  isSaving ||
                  !draft.newLangCode.trim() ||
                  !draft.newLangContent.trim()
                }
                onClick={onAddLang}
              >
                {isSaving ? (
                  <CircularProgress size={14} />
                ) : (
                  t("settings.translations.add")
                )}
              </Button>
            </Box>
          </Box>
        )}
      </Collapse>
      <Dialog
        open={deleteLang !== null}
        onClose={() => setDeleteLang(null)}
        aria-labelledby="delete-translation-dialog-title"
        aria-describedby="delete-translation-dialog-description"
      >
        <DialogTitle id="delete-translation-dialog-title">
          {t("settings.translations.deleteTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-translation-dialog-description">
            {t("settings.translations.deleteDescription", {
              language: getLanguageInfo(deleteLang ?? "")?.name ?? deleteLang,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteLang(null)}>
            {t("settings.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              if (deleteLang) onDeleteLang(deleteLang);
              setDeleteLang(null);
            }}
          >
            {t("settings.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateTranslations;
